import { useState, useRef, useEffect, lazy, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, LogOut, Shield } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminRole } from "@/hooks/useAdminRole";
import { useCart } from "@/hooks/useCart";
import { useTranslations } from "@/hooks/useTranslations";
import LanguageSelector from "./LanguageSelector";
import CurrencySelector from "./CurrencySelector";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Lazy-load heavy sub-components to reduce initial bundle
const AuthDialog = lazy(() => import("./AuthDialog"));
const MobileNav = lazy(() => import("./MobileNav"));
const ProductSearch = lazy(() => import("./ProductSearch"));

const Header = () => {
  const navigate = useNavigate();
  const { t } = useTranslations();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user } = useAuth();
  const { isAdmin } = useAdminRole();
  const { itemCount } = useCart();
  const navRef = useRef<HTMLElement>(null);

  // Ensure a profile row exists for OAuth users
  useEffect(() => {
    if (!user) return;

    const timeout = window.setTimeout(async () => {
      try {
        const md = (user.user_metadata ?? {}) as Record<string, unknown>;

        let firstName: string | null =
          (typeof md.first_name === "string" && md.first_name) ||
          (typeof md.given_name === "string" && md.given_name) ||
          (typeof (md.name as any)?.firstName === "string" && (md.name as any).firstName) ||
          null;
        let lastName: string | null =
          (typeof md.last_name === "string" && md.last_name) ||
          (typeof md.family_name === "string" && md.family_name) ||
          (typeof (md.name as any)?.lastName === "string" && (md.name as any).lastName) ||
          null;

        if (!firstName && !lastName) {
          const fullName =
            (typeof md.full_name === "string" && md.full_name) ||
            (typeof md.name === "string" && md.name) ||
            null;
          if (fullName) {
            const parts = fullName.trim().split(/\s+/);
            firstName = parts[0] || null;
            lastName = parts.length > 1 ? parts.slice(1).join(" ") : null;
          }
        }

        if (firstName || lastName) {
          await supabase.from("profiles").upsert(
            { id: user.id, first_name: firstName, last_name: lastName },
            { onConflict: "id" }
          );
        } else {
          await supabase.from("profiles").upsert(
            { id: user.id },
            { onConflict: "id", ignoreDuplicates: true }
          );
        }
      } catch {
        // Silent: profile creation is best-effort
      }
    }, 0);

    return () => window.clearTimeout(timeout);
  }, [user]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const handleSearchClick = () => {
    setIsSearchOpen(prev => !prev);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast.success("Signed out successfully");
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="container flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="font-display text-2xl md:text-3xl tracking-wider">
            UNION
          </Link>
          
          {/* Navigation */}
          <nav 
            ref={navRef}
            className="hidden md:flex items-center gap-8 relative"
            aria-label="Main navigation"
          >
            {/* Nav Links */}
            <div className={`flex items-center gap-8 transition-all duration-300 ease-out ${
              isSearchOpen 
                ? "opacity-0 pointer-events-none absolute -translate-x-4" 
                : "opacity-100 translate-x-0"
            }`}>
              <Link
                to="/new" 
                className="text-sm uppercase tracking-widest text-foreground hover:text-muted-foreground transition-smooth"
              >
                {t("nav.new_arrivals")}
              </Link>
              <Link 
                to="/collection" 
                className="text-sm uppercase tracking-widest text-foreground hover:text-muted-foreground transition-smooth"
              >
                {t("nav.collection")}
              </Link>
              <Link 
                to="/about" 
                className="text-sm uppercase tracking-widest text-foreground hover:text-muted-foreground transition-smooth"
              >
                {t("nav.about")}
              </Link>
            </div>
            
            {/* Search Bar */}
            <div className={`transition-all duration-300 ease-out ${
              isSearchOpen 
                ? "opacity-100 w-64" 
                : "opacity-0 w-0 pointer-events-none"
            }`}>
              {isSearchOpen && (
                <Suspense fallback={<div className="w-64 h-10" />}>
                  <ProductSearch 
                    isOpen={isSearchOpen} 
                    onClose={() => setIsSearchOpen(false)}
                    autoFocus={isSearchOpen}
                  />
                </Suspense>
              )}
            </div>
          </nav>
          
          {/* Actions */}
          <div className="flex items-center gap-3 md:gap-5">
            <div className="hidden md:flex items-center gap-3">
              <LanguageSelector />
              <CurrencySelector />
            </div>
            <button 
              onClick={handleSearchClick}
              className="text-sm uppercase tracking-widest text-foreground hover:text-muted-foreground transition-smooth hidden md:block"
              aria-label={isSearchOpen ? "Close search" : "Open search"}
            >
              {isSearchOpen ? t("nav.exit") : t("nav.search")}
            </button>
            <Link 
              to="/cart" 
              className="text-sm uppercase tracking-widest text-foreground hover:text-muted-foreground transition-smooth"
              aria-label={`Cart (${itemCount})`}
            >
              {t("nav.cart")} ({itemCount})
            </Link>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="text-foreground hover:text-muted-foreground transition-smooth">
                  <User className="w-5 h-5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate("/profile")}>
                    <User className="w-4 h-4 mr-2" />
                    {t("profile.title")}
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate("/admin")}>
                      <Shield className="w-4 h-4 mr-2" />
                      {t("profile.admin")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="w-4 h-4 mr-2" />
                    {t("auth.sign_out")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button
                onClick={() => setAuthDialogOpen(true)}
                className="text-foreground hover:text-muted-foreground transition-smooth"
                title="Sign in"
              >
                <User className="w-5 h-5" />
              </button>
            )}
            
            {/* Mobile menu button */}
            <button 
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden text-foreground"
              aria-label="Open menu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>
      </header>
      {authDialogOpen && (
        <Suspense fallback={null}>
          <AuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} />
        </Suspense>
      )}
      {isMobileMenuOpen && (
        <Suspense fallback={null}>
          <MobileNav
            isOpen={isMobileMenuOpen}
            onClose={() => setIsMobileMenuOpen(false)}
            user={user}
            onSignOut={handleSignOut}
            onAuthDialogOpen={() => setAuthDialogOpen(true)}
          />
        </Suspense>
      )}
    </>
  );
};

export default Header;
